
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link as LinkIcon, Video, FileText, Pen, Trash, Image, Code, ImageOff } from 'lucide-react';
import { SubTopic } from '@/types/resource';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

interface SubTopicCardProps {
  subtopic: SubTopic;
  resourceId: string;
  resourceType: 'video' | 'document' | 'link';
  isAdmin: boolean;
  onEdit: (subtopic: SubTopic) => void;
  onDelete: (subtopic: SubTopic) => void;
  onEmbedCode: (subtopic: SubTopic) => void;
  onImageUpload: (subtopic: SubTopic) => void;
}

const SubTopicCard: React.FC<SubTopicCardProps> = ({ 
  subtopic, 
  resourceId,
  resourceType, 
  isAdmin, 
  onEdit, 
  onDelete,
  onEmbedCode,
  onImageUpload
}) => {
  const [isEditTitleDialogOpen, setIsEditTitleDialogOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState(subtopic.title);
  const [editedDescription, setEditedDescription] = useState(subtopic.description || '');
  const [imageError, setImageError] = useState(false);
  
  // Reset image error state when subtopic changes
  useEffect(() => {
    setImageError(false);
  }, [subtopic.imageUrl]);

  // Update local state when subtopic changes
  useEffect(() => {
    setEditedTitle(subtopic.title);
    setEditedDescription(subtopic.description || '');
  }, [subtopic]);

  const getSubtopicIcon = () => {
    switch (resourceType) {
      case 'video': return <Video className="h-5 w-5 text-brand-blue" />;
      case 'document': return <FileText className="h-5 w-5 text-brand-blue" />;
      case 'link': 
      default: return <LinkIcon className="h-5 w-5 text-brand-blue" />;
    }
  };

  const handleSaveTitle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Create a new subtopic object with the updated title and description
    const updatedSubtopic = {
      ...subtopic,
      title: editedTitle,
      description: editedDescription
    };
    
    // Call the parent's onEdit function with the updated subtopic
    onEdit(updatedSubtopic);
    
    // Close the dialog
    setIsEditTitleDialogOpen(false);
    
    // Show success message
    toast.success("Topic updated successfully");
  };

  const handleImageError = () => {
    setImageError(true);
  };
  
  // Check for valid image URL
  const isValidImageUrl = (url: string) => {
    return url && 
           typeof url === 'string' && 
           (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:image'));
  };
  
  // Ensure the imageUrl is valid and not a blob URL
  const imageUrl = subtopic.imageUrl && isValidImageUrl(subtopic.imageUrl) && !subtopic.imageUrl.startsWith('blob:') 
    ? subtopic.imageUrl 
    : null;

  return (
    <Card className="hover:shadow-md transition-shadow duration-300">
      <CardHeader className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {imageUrl && !imageError ? (
              <Avatar className="h-8 w-8 mr-2">
                <AvatarImage 
                  src={imageUrl} 
                  alt={subtopic.title} 
                  onError={handleImageError}
                />
                <AvatarFallback>{getSubtopicIcon()}</AvatarFallback>
              </Avatar>
            ) : (
              getSubtopicIcon()
            )}
            <div className="flex flex-col ml-2">
              <CardTitle className="text-lg">{subtopic.title}</CardTitle>
              {isAdmin && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-gray-500 hover:text-brand-blue -ml-2 -mt-1 p-0"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditedTitle(subtopic.title);
                    setEditedDescription(subtopic.description || '');
                    setIsEditTitleDialogOpen(true);
                  }}
                >
                  <Pen className="h-3 w-3 mr-1" /> Edit title
                </Button>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onImageUpload(subtopic);
                }}
                className="text-gray-500 hover:text-brand-blue"
                title="Upload image"
              >
                <Image className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEmbedCode(subtopic);
                }}
                className="text-gray-500 hover:text-brand-blue"
                title="Embed code"
              >
                <Code className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(subtopic);
                }}
                className="text-gray-500 hover:text-brand-blue"
                title="Edit topic"
              >
                <Pen className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(subtopic);
                }}
                className="text-gray-500 hover:text-red-500"
                title="Delete topic"
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <CardDescription>{subtopic.description}</CardDescription>
        
        {/* Display topic image if available */}
        {imageUrl && !imageError ? (
          <div className="mt-4 border rounded-md overflow-hidden">
            <img 
              src={imageUrl} 
              alt={subtopic.title} 
              className="w-full h-auto object-cover max-h-40"
              onError={handleImageError} 
            />
          </div>
        ) : imageUrl && imageError ? (
          <div className="mt-4 border rounded-md overflow-hidden flex items-center justify-center bg-gray-100 h-40">
            <div className="flex flex-col items-center text-gray-400">
              <ImageOff className="h-10 w-10 mb-2" />
              <span className="text-sm">Image not available</span>
            </div>
          </div>
        ) : null}
        
        {subtopic.htmlEmbed && (
          <div className="mt-4">
            <div dangerouslySetInnerHTML={{ __html: subtopic.htmlEmbed }} />
          </div>
        )}
      </CardContent>

      {/* Dialog for editing title and description */}
      <Dialog open={isEditTitleDialogOpen} onOpenChange={setIsEditTitleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Topic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input 
                id="title"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description"
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditTitleDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTitle} 
              className="bg-brand-blue hover:bg-brand-darkBlue"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SubTopicCard;
